import { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import './GretaContactForm.css';

const PABBLY_WEBHOOK_URL = import.meta.env.VITE_PABBLY_WEBHOOK_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: 'Accounting & bookkeeping',
    message: '',
  });

  const [status, setStatus] = useState('idle');

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (status === 'submitting') return;

    if (!PABBLY_WEBHOOK_URL) {
      console.error('Pabbly webhook URL is not configured.');
      setStatus('error');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(PABBLY_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          interest: formData.interest,
          message: formData.message.trim(),
          source: 'ProfitYO Global Contact Form',
          page_url: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error(`Pabbly returned ${response.status}`);
      }

      setStatus('success');

      setFormData({
        name: '',
        email: '',
        interest: 'Accounting & bookkeeping',
        message: '',
      });
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setStatus('error');
    }
  };

  return (
    <section className="contact-page">
      <div className="container contact-grid">
        <div className="contact-copy">
          <span className="eyebrow">Let's connect</span>

          <h1>
            Bring us your
            <br />
            <em>next big idea.</em>
          </h1>

          <p>
            Whether you need a cleaner financial foundation, smarter workflows,
            or a technology partner for your next build, we would love to hear
            what you are working toward.
          </p>

          <div className="contact-detail">
            <SafeIcon icon={FiIcons.FiMail} />
            <div>
              <small>Email:</small>
              <a href="mailto:inquiries@profityoglobal.com">
                inquiries@profityoglobal.com
              </a>
            </div>
          </div>

          <div className="contact-detail">
            <SafeIcon icon={FiIcons.FiGlobe} />
            <div>
              <small>Serving businesses</small>
              <span>Across the US and globally</span>
            </div>
          </div>

          <div className="social-links">
            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              <SafeIcon icon={FiIcons.FiLinkedin} />
            </a>

            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <SafeIcon icon={FiIcons.FiInstagram} />
            </a>

            <a
              href="mailto:inquiries@profityoglobal.com"
              aria-label="Email"
            >
              <SafeIcon icon={FiIcons.FiMail} />
            </a>
          </div>
        </div>

        <div className="contact-panel">
          <div className="form-heading">
            <span>Consultation request · Business inquiry</span>
            <h2>Tell us what is next.</h2>
          </div>

          {status === 'success' ? (
            <div className="contact-success">
              <div className="contact-success-icon">
                <SafeIcon icon={FiIcons.FiCheckCircle} />
              </div>

              <h3>Thank you for reaching out.</h3>

              <p>
                Your inquiry has been received. Our team will get back to you
                soon.
              </p>

              <button
                type="button"
                className="contact-reset-button"
                onClick={() => setStatus('idle')}
              >
                Send another inquiry
              </button>
            </div>
          ) : (
            <form
              className="contact-greta-form"
              onSubmit={handleSubmit}
            >
              <div className="form-field">
                <label htmlFor="contact-name">Name</label>

                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-field">
                <label htmlFor="contact-email">Work email</label>

                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              <div className="form-field">
                <label htmlFor="contact-interest">
                  How can we help?
                </label>

                <select
                  id="contact-interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                  disabled={status === 'submitting'}
                >
                  <option value="Accounting & bookkeeping">
                    Accounting & bookkeeping
                  </option>

                  <option value="Tax services">
                    Tax services
                  </option>

                  <option value="AI & automation">
                    AI & automation
                  </option>

                  <option value="Business advisory">
                    Business advisory
                  </option>

                  <option value="Technology / software">
                    Technology / software
                  </option>

                  <option value="Other">
                    Other
                  </option>
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="contact-message">
                  Tell us a little more
                </label>

                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Tell us about your project, goals, or what you need help with..."
                  value={formData.message}
                  onChange={handleChange}
                  autoComplete="off"
                  rows="6"
                  required
                  disabled={status === 'submitting'}
                />
              </div>

              {status === 'error' && (
                <div className="contact-error" role="alert">
                  <SafeIcon icon={FiIcons.FiAlertCircle} />

                  <span>
                    Your inquiry could not be sent. Please try again or email
                    us directly at{' '}
                    <a href="mailto:inquiries@profityoglobal.com">
                      inquiries@profityoglobal.com
                    </a>
                    .
                  </span>
                </div>
              )}

              <button
                type="submit"
                className="contact-submit-button"
                disabled={status === 'submitting'}
              >
                {status === 'submitting'
                  ? 'Sending...'
                  : 'Send inquiry'}
              </button>
            </form>
          )}

          <small className="form-note">
            Your inquiry will be sent securely to our team.
          </small>
        </div>
      </div>
    </section>
  );
}